import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { IntegratorForm } from '../components/forms/IntegratorForm';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { usePagination } from '../hooks/usePagination';
import { apiList, apiCreate } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { deleteIntegrator } from '../api/integrators';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';
import { refreshAllCounters } from '../utils/refreshCounters';
import { isAdminHost } from '../utils/host';
import { CountBand } from '../components/ui/CountBand';
import { ListFilterSelect } from '../components/ui/ListFilterSelect';
import { EntityMapView } from '../components/ui/EntityMapView';
import { BrandLoader } from '../components/ui/BrandLoader';

type ViewMode = 'list' | 'grid' | 'map';

export const Integrators: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [integrators, setIntegrators] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [quickViewDealer, setQuickViewDealer] = useState<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('view:integrators') as ViewMode) || 'list');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();
  // Monthly revenue is sensitive — only super_admin sees the column.
  const { user } = useAuth() as any;
  const showMonthly = isSuperAdmin(user?.role || 'customer');

  const searchFields = useCallback((i: any) => [i.company, i.name, i.email, i.phone], []);
  const dealerFilter = useCallback((i: any) => statusFilter === 'all' || String(i.status || 'active').toLowerCase() === statusFilter, [statusFilter]);
  const { search, setSearch, page, setPage, totalPages, filteredData, paginatedData, totalFiltered, startIndex, endIndex } = usePagination(integrators, searchFields, dealerFilter);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const intData = await apiList('integrators');

      setIntegrators((intData || []).map((i: any) => ({
        ...i,
        company: i.company || i.company_name || i.name,
        email: i.email || i.contact_email || '',
      })));
    } catch (e) {
      console.error('Failed to load integrators:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data: any) => {
    await apiCreate('integrators', {
      name: data.name,
      company_name: data.name,
      email: data.email,
      phone: data.phone,
      status: 'active',
    });
    refreshAllCounters();
    setShowModal(false);
    setLoading(true);
    await load();
  };

  const columns = [
    { key: '_select', header: <input type="checkbox" checked={selected.size === integrators.length && integrators.length > 0} onChange={() => toggleAll(integrators.map(d => d.id))} className="w-4 h-4 rounded border-gray-300" />, render: (i: any) => <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />, className: 'w-8' },
    { key: 'company', header: 'Company', render: (i: any) => <div><p className="font-medium text-gray-900">{i.company}</p>{i.name !== i.company && <p className="text-xs text-gray-500">{i.name}</p>}</div> },
    { key: 'total_customers',          header: 'Customers',    render: (i: any) => i.total_customers          || 0 },
    { key: 'total_properties',         header: 'Properties',   render: (i: any) => i.total_properties         || 0 },
    { key: 'total_units',              header: 'Units',        render: (i: any) => i.total_units              || 0 },
    { key: 'total_intercoms',          header: 'Intercoms',    render: (i: any) => i.total_intercoms          || 0 },
    { key: 'total_systems',            header: 'ACS Systems',  render: (i: any) => i.total_systems            || 0 },
    { key: 'total_devices',            header: 'Devices',
      render: (i: any) => (Number(i.total_devices) || 0) + (Number(i.total_acs_devices) || 0) },
    { key: 'total_tenants',            header: 'Tenants',      render: (i: any) => i.total_tenants            || 0 },
    { key: 'total_guests',             header: 'Guests',       render: (i: any) => i.total_guests             || 0 },
    { key: 'total_phones',             header: 'Phones',       render: (i: any) => i.total_phones             || 0 },
    { key: 'total_active_activations', header: 'Mobile Users', render: (i: any) => i.total_active_activations ?? i.total_activations ?? 0 },
    { key: 'status', header: 'Status', render: (i: any) => <Badge status={i.status || 'active'}>{i.status || 'active'}</Badge> },
    { key: '_actions', header: '', render: (i: any) => (
      <div onClick={(e) => e.stopPropagation()}><button onClick={() => navigate(`/integrators/${i.uuid || i.id}`)} className="row-open">Open <span aria-hidden="true">→</span></button></div>
    ), className: 'w-24' },
  ];

  if (loading) return <BrandLoader label="Loading dealers" />;

  return (
    <div className="shadow-list-page has-prototype-toolbar">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">Dealers</h1><p className="text-gray-500 mt-1">Manage dealer partners</p></div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => { setViewMode('list'); localStorage.setItem('view:integrators', 'list'); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              title="List View"
            >List</button>
            <button
              onClick={() => { setViewMode('grid'); localStorage.setItem('view:integrators', 'grid'); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              title="Grid View"
            >Grid</button>
          </div>
          <Button onClick={() => setShowModal(true)}><svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>Add Dealer</Button>
        </div>
      </div>
      <CountBand value={totalFiltered} detail="Dealers in the current accessible portfolio" />
      <div className="prototype-toolbar"><SearchInput value={search} onChange={setSearch} placeholder="Search dealers..."/><label><small>Status</small><select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label><div className="prototype-view-toggle"><button className={viewMode==='list'?'active':''} onClick={()=>{setViewMode('list');localStorage.setItem('view:integrators','list')}}>Table</button><button className={viewMode==='grid'?'active':''} onClick={()=>{setViewMode('grid');localStorage.setItem('view:integrators','grid')}}>Grid</button><button className={viewMode==='map'?'active':''} onClick={()=>{setViewMode('map');localStorage.setItem('view:integrators','map')}}>Map</button></div><Button onClick={()=>setShowModal(true)}>Add Dealer</Button></div>
      {/* Mobile: card grid (no list/table on small screens). */}
      <div className="md:hidden grid grid-cols-1 gap-4 mb-4">
        {paginatedData.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">No dealers found</div>
        ) : (
          paginatedData.map((i: any) => {
            const companyName = i.company || i.company_name || i.name;
            const contactName = [i.contact_first_name, i.contact_last_name].filter(Boolean).join(' ') || i.contact_name || (i.name !== companyName ? i.name : '');
            const addressLine = [i.address || i.address1, i.address2, i.city, i.state, i.zip || i.zip_code || i.postal_code].filter(Boolean).join(', ');
            const contactEmail = i.contact_email || i.email;
            const contactPhone = i.contact_phone || i.phone;
            return (
            <div key={i.id} onClick={() => setQuickViewDealer(i)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{companyName}</h3>
                </div>
                <Badge status={i.status || 'active'}>{i.status || 'active'}</Badge>
              </div>
              <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
              <div className="customer-card-contact">
                <span>Primary contact</span><strong>{contactName || 'No contact on file'}</strong>
                {contactEmail && <a href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()}>{contactEmail}</a>}
                {contactPhone && <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()}>{contactPhone}</a>}
              </div>
              <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}>
                <summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary>
                <div><article><span>Customers</span><strong>{i.total_customers || 0}</strong></article><article><span>Properties</span><strong>{i.total_properties || 0}</strong></article><article><span>Tenants</span><strong>{i.total_tenants || 0}</strong></article><article><span>Devices</span><strong>{(Number(i.total_devices) || 0) + (Number(i.total_acs_devices) || 0)}</strong></article><article><span>Intercoms</span><strong>{i.total_intercoms || 0}</strong></article><article><span>Mobile</span><strong>{i.total_active_activations ?? i.total_activations ?? 0}</strong></article></div>
              </details>
              <div className="mobile-card-actions">
                <button onClick={(e) => { e.stopPropagation(); navigate(`/integrators/${i.uuid || i.id}`); }} className="row-open">Open <span aria-hidden="true">→</span></button>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Desktop: full searchable table or grid (hidden on small screens). */}
      {viewMode === 'list' ? (
        <Card padding={false} className="hidden md:block">
          <Table
            columns={columns}
            data={paginatedData}
            onRowClick={(i: any) => navigate(`/integrators/${i.uuid || i.id}`)}
            emptyMessage="No dealers found"
          />
          <Pagination page={page} totalPages={totalPages} totalItems={totalFiltered} startIndex={startIndex} endIndex={endIndex} onPageChange={setPage} />
        </Card>
      ) : viewMode === 'map' ? (
        <EntityMapView addressLabel="company office address" onOpen={navigate} items={filteredData.map((i:any) => ({ id:String(i.uuid || i.id), name:i.company || i.company_name || i.name || 'Dealer', address:[i.address || i.address1,i.city,i.state,i.zip || i.zip_code].filter(Boolean).join(', ') || i.billing_address || '', contact:i.contact_name || [i.contact_first_name,i.contact_last_name].filter(Boolean).join(' '), email:i.contact_email || i.email, phone:i.contact_phone || i.phone, status:i.status, lat:Number(i.latitude ?? i.lat ?? i.address_lat), lng:Number(i.longitude ?? i.lng ?? i.lon ?? i.address_lng), detailPath:`/integrators/${i.uuid || i.id}`, metrics:[{label:'Customers',value:i.total_customers||0},{label:'Properties',value:i.total_properties||0},{label:'Units',value:i.total_units||0},{label:'Devices',value:(Number(i.total_devices)||0)+(Number(i.total_acs_devices)||0)},{label:'Intercoms',value:i.total_intercoms||0},{label:'Mobile',value:i.total_active_activations??i.total_activations??0}] }))} />
      ) : (
        <div className="hidden md:block">
          {paginatedData.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">No dealers found</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedData.map((i: any) => {
                const companyName = i.company || i.company_name || i.name;
                const contactName = [i.contact_first_name, i.contact_last_name].filter(Boolean).join(' ') || i.contact_name || (i.name !== companyName ? i.name : '');
                const addressLine = [i.address || i.address1, i.address2, i.city, i.state, i.zip || i.zip_code || i.postal_code].filter(Boolean).join(', ');
                const contactEmail = i.contact_email || i.email;
                const contactPhone = i.contact_phone || i.phone;
                return (
                <div key={i.id} onClick={() => navigate(`/integrators/${i.uuid || i.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{companyName}</h3>
                    </div>
                    <Badge status={i.status || 'active'}>{i.status || 'active'}</Badge>
                  </div>
                  <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
                  <div className="customer-card-contact">
                    <span>Primary contact</span><strong>{contactName || 'No contact on file'}</strong>
                    {contactEmail && <a href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()}>{contactEmail}</a>}
                    {contactPhone && <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()}>{contactPhone}</a>}
                  </div>
                  <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}>
                    <summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary>
                    <div><article><span>Customers</span><strong>{i.total_customers || 0}</strong></article><article><span>Properties</span><strong>{i.total_properties || 0}</strong></article><article><span>Tenants</span><strong>{i.total_tenants || 0}</strong></article><article><span>Devices</span><strong>{(Number(i.total_devices) || 0) + (Number(i.total_acs_devices) || 0)}</strong></article><article><span>Intercoms</span><strong>{i.total_intercoms || 0}</strong></article><article><span>Mobile</span><strong>{i.total_active_activations ?? i.total_activations ?? 0}</strong></article></div>
                  </details>
                </div>
                );
              })}
            </div>
          )}
          <div className="mt-4">
            <Pagination page={page} totalPages={totalPages} totalItems={totalFiltered} startIndex={startIndex} endIndex={endIndex} onPageChange={setPage} />
          </div>
        </div>
      )}
      <Modal open={!!quickViewDealer} onClose={() => setQuickViewDealer(null)} title={`${quickViewDealer?.name || 'Dealer'} — Quick View`} size="lg">
        {quickViewDealer && (() => {
          const i = quickViewDealer;
          const addressLine = [i.address, i.city, i.state, i.zip].filter(Boolean).join(', ');
          const num = (n: any) => Number(n || 0).toLocaleString();
          const MetricRow = ({ label, month, lifetime, muted }: { label: string; month?: any; lifetime?: any; muted?: boolean }) => (
            <div className={`grid grid-cols-[1fr_auto_auto] items-baseline gap-3 py-1.5 ${muted ? 'text-gray-400' : ''}`}>
              <span className="text-sm">{label}</span>
              <span className={`text-sm font-semibold tabular-nums w-12 text-right ${muted ? '' : 'text-gray-900'}`}>{num(month)}</span>
              <span className="text-xs text-gray-500 tabular-nums w-16 text-right">{lifetime !== undefined ? num(lifetime) : ''}</span>
            </div>
          );
          const ColHeader = ({ title }: { title: string }) => (
            <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-3 mb-1 pb-1 border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{title}</p>
              <p className="text-[10px] font-semibold uppercase text-gray-400 w-12 text-right">MTD</p>
              <p className="text-[10px] font-semibold uppercase text-gray-400 w-16 text-right">Lifetime</p>
            </div>
          );
          return (
            <div className="space-y-5">
              {/* ─── Hero Cards ─── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Properties</p>
                    <p className="text-3xl font-extrabold text-gray-900">{num(i.total_properties)}</p>
                  </div>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Customers</p>
                    <p className="text-3xl font-extrabold text-gray-900">{num(i.total_customers)}</p>
                  </div>
                </div>
              </div>

              {/* ─── Three column metrics ─── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:divide-x md:divide-gray-200">
                <div className="md:pr-6">
                  <ColHeader title="Users & Activity" />
                  <MetricRow label="Tenants" month={i.total_tenants} />
                  <MetricRow label="Guests" month={i.total_guests} />
                  <MetricRow label="Mobile Apps" month={i.total_activations} muted={!i.total_activations} />
                  <MetricRow label="ACS Systems" month={i.total_systems} muted={!i.total_systems} />
                  <MetricRow label="Devices" month={i.total_devices} />
                  <MetricRow label="Phone Numbers" month={i.total_phones} />
                </div>
                <div className="md:px-6">
                  <ColHeader title="Usage Metrics" />
                  <MetricRow label="Unlocks" month={i.door_unlocks_this_month} lifetime={i.door_unlocks_lifetime} />
                  <MetricRow label="Page Views" month={i.visitor_page_views_this_month} lifetime={i.visitor_page_views_lifetime} />
                  <MetricRow label="Intercom Calls" month={i.intercom_calls_this_month} lifetime={i.intercom_calls_lifetime} />
                  <MetricRow label="Face Scans" month={i.face_id_verifications_this_month} lifetime={i.face_id_verifications_lifetime} />
                  <MetricRow label="BLE Reads" month={i.ble_credential_reads_this_month} lifetime={i.ble_credential_reads_lifetime} />
                  <MetricRow label="Cellular MB" month={i.cellular_data_mb_this_month} lifetime={i.cellular_data_mb_lifetime} />
                </div>
                <div className="md:pl-6">
                  <ColHeader title="Communication" />
                  <MetricRow label="Video" month={i.video_minutes_this_month} lifetime={i.video_minutes_lifetime} />
                  <MetricRow label="Voice" month={i.voice_minutes_this_month} lifetime={i.voice_minutes_lifetime} />
                  <MetricRow label="SMS" month={i.sms_sent_this_month} lifetime={i.sms_sent_lifetime} />
                  <MetricRow label="Pushes" month={i.push_notifications_sent_this_month} lifetime={i.push_notifications_sent_lifetime} />
                </div>
              </div>

              {/* ─── Contact Strip ─── */}
              <div className="border-t border-gray-200 pt-4 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Contact</p>
                  <p className="font-medium text-gray-900">{i.contact_name || i.contact_first_name || i.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900 truncate">{i.email || i.contact_email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{i.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="font-medium text-gray-900">{i.created_at ? formatDate(i.created_at) : '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">{addressLine || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Stripe</p>
                  <p className="font-mono text-xs text-gray-900 truncate">{i.stripe_customer_id || <span className="text-yellow-600">—</span>}</p>
                </div>
                {isAdminHost() && (
                  <div>
                    <p className="text-xs text-gray-500">Open Balance</p>
                    <p className={`font-medium ${(i.open_balance || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>${num(i.open_balance)}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-3 border-t border-gray-200">
                <button onClick={() => navigate(`/integrators/${i.uuid || i.id}`)} className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">View Full Details</button>
                <button onClick={() => navigate(`/integrators/${i.uuid || i.id}?edit=true`)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">Edit</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Dealer">
        <IntegratorForm onSubmit={handleCreate} onCancel={() => setShowModal(false)} />
      </Modal>
      <BulkActions selectedIds={selected} onClearSelection={clearSelection} onDelete={async (ids) => { await Promise.all(ids.map(id => deleteIntegrator(id))); refreshAllCounters(); setLoading(true); await load(); }} entityName="dealers" />
    </div>
  );
};
